/**
 * cells — what the wall paints, in what order (`ui/cells.ts`) — the canon of BRIEF § Iteration II
 * ("the wall remembers you"), pinned here because two operator-verified defects in a row lived in
 * this derivation before it: a chain painted twice (R3), and the arrival block carrying
 * from-scratch chains only (R6).
 *
 * The pin (plan `2026_09_04_wall_pin.md`, BRIEF Iteration II §4): a fourth cell kind, `pinned`, one
 * per pinned TAKE rather than per chain — `/mine.pinned`, not `/mine.done`. Placed by the same day
 * rule as a chain cell, clocked on `pinnedAt` instead of `seen_at`, and skipped outright when the
 * pinned take is currently its chain's FACE (`ownDone`'s own row for that chain) — the chain's own
 * cell already shows it, and a second cell would paint the same picture twice. Within a day, every
 * own cell — chain or pinned — sorts by its OWN clock descending (a chain by `seen_at`, null first;
 * a pinned cell by `pinnedAt`, which is never null), so a take pinned this afternoon stands ahead of
 * a chain merely seen this morning. Never stamped by `/seen` (a pin is not an arrival).
 *
 * The two orderings (BRIEF §3, amended 2026-09-04):
 *
 *   at rest — the ghost first, always (signed in or not, chains or none — BRIEF Iteration II §2/7).
 *   Signed out, or signed in with nothing of one's own: the ghost, then `default_rank` as the wire
 *   sent it (BRIEF §8) — unchanged from before Iteration II. Signed in with own chains: every own
 *   chain (`/mine.done`, one row per chain) is bucketed by DAY — `floor((now - seen_at) / 1 day)`,
 *   clamped to zero, `seen_at` null counting as day 0 — and day d's group is inserted at position
 *   `T × d` of the catalog order (day 0 always at position 0), or immediately after the previous
 *   group's end if that is later (`DAY_GROUP_SPACING` below, a dial). Within a
 *   day, every own cell sorts by its OWN clock descending (plan `2026_09_04_wall_pin.md`'s "Order
 *   inside a day"): a chain by `seen_at` (null — unseen — sorts first), a pinned cell by `pinnedAt`
 *   (never null); two unseen chains tie-break by `created_at` descending, the rule this sort always
 *   carried. A chain rooted on a tile stays at ITS
 *   OWN catalog position when that is earlier than the day's target — only a chain whose tile sits
 *   later gets lifted out of the catalog into the group; a scratch chain has no catalog position of
 *   its own and always lifts. `now` is read ONCE per entry (`Wall.tsx`'s held snapshot, never
 *   recomputed per render), which is what keeps a chain from moving while the page stays open: a
 *   stamp landing mid-session sets `seen_at` to a moment AFTER that frozen `now`, so the age clamps
 *   to zero and the row stays exactly where it was already painted, in day 0. A `?batch=` entry
 *   (plan e — the page verb's landing) leads day 0 in POSITION order (its own `created_at` values
 *   are too close together for the day sort to keep the nine in the order the pack asked for), and
 *   is excluded from the ordinary day-0 scan by chain key so it is never placed twice.
 *
 *   under a query — the ghost first, carrying the typed words (validated 2026-09-04 evening: the
 *   words themselves are the closest thing to the words, so by the distance-from-you rule the
 *   ghost is position 0 under a query exactly as at rest; it stood at the seam until then and was
 *   below the fold whenever the matches filled a screen), then the user's own matched chains
 *   (armed or from-scratch alike, newest first — `/mine?q=`'s `matched`,
 *   `2026_09_01_search_own_chains.md`), then the catalog's own matched tiles (`GET /wall`'s own
 *   segment), then the rest. The seam is no longer marked: the drift from the matched segment into
 *   the rest is silent, which is what "re-orders, never filters" already means.
 *
 * Held per entry (plan `2026_09_26_wall_hold_still_pill.md`): the caller hands this function a
 * SNAPSHOT of `/mine` taken at an entry (page load, a query swap, the first own-matched answer, a
 * `?batch=` landing, sign-in or sign-out), never the live poll — so a take finishing within an entry
 * swaps its picture inside the cell it already has and moves nothing. What has no cell in that
 * placement and comes to exist within the entry is BORN (`bornTakes` below): placed right after the
 * ghost, newest first, and held there until the next entry, which gathers it into its day.
 *
 * One cell per chain throughout (`chains.collapseChains`): earlier takes of a chain are reachable
 * through the column's counter, never as a second cell.
 */
import type {AnonTile, AnonWallResponse, Take, TileSummary} from '@core/wall/contract'
import {at, chainFaces, chainKeyOf, collapseChains, isFace, type ChainKey} from './chains'
import type {LayoutTile} from './justify'

/** A cell of the wall, whatever it is made of. `LayoutTile` is all `justify.ts` needs from it.
 *
 *  `anon` is the visitor's cell in a house that closes itself (`HouseTheme.visitor`): it carries an
 *  `AnonTile` and nothing else, and `AnonTile`'s own union is the no-leak guarantee — a veiled
 *  tile's shape has no `image_key` to serve and no `pack_slug`/`title`/`chips` field to render, so
 *  "no estate text on a visitor's tile" is a property of the type, never of a convention about
 *  empty strings. */
export type WallCell = LayoutTile &
    (
        | {kind: 'tile'; key: string; tile: TileSummary}
        | {kind: 'anon'; key: string; tile: AnonTile}
        | {kind: 'scratch'; key: string; take: Take}
        | {kind: 'pinned'; key: string; take: Take}
        | {kind: 'ghost'; key: string}
    )

function tileCellKey(tileId: number): string {
    return `t${tileId}`
}

function scratchCellKey(rootId: string): string {
    return `s${rootId}`
}

function pinnedCellKey(takeId: string): string {
    return `p${takeId}`
}

export function tileCell(tile: TileSummary): WallCell {
    return {
        kind: 'tile', key: tileCellKey(tile.id), tile,
        width: tile.width, height: tile.height,
    }
}

/** A from-scratch cell is sized by its own stored dimensions (S10) — the ratio the user picked at
 *  the ghost, which the chain then keeps. */
export function scratchCell(take: Take): WallCell {
    return {
        kind: 'scratch', key: scratchCellKey(take.root_id), take,
        width: take.width, height: take.height,
    }
}

/** A pinned cell — a VIEW of one specific take, drawn at that take's own dimensions whether its
 *  chain is rooted on a tile or is from scratch (BRIEF Iteration II §4: "own ratio"). Keyed on the
 *  TAKE, not the chain, so it can never collide with that chain's own `tileCell`/`scratchCell` key
 *  even though both may be on screen at once (the chain's face, and an older pinned step). */
export function pinnedCell(take: Take): WallCell {
    return {
        kind: 'pinned', key: pinnedCellKey(take.id), take,
        width: take.width, height: take.height,
    }
}

/** The wall cell key a chain paints as, when it has one (`tileCell`/`scratchCell`'s own key) — a
 *  CHAIN key (`chains.tileChainKey`/`scratchChainKey`, e.g. `tile:12`) names the chain's identity;
 *  this names the CELL it paints as on the wall (`t12`), a deliberately different string so the
 *  two can never be confused by a stray substring match. `Wall.tsx`'s Continue ↑ uses it to find a
 *  chain's row before scrolling to it (Iteration II §3) — the ghost has no cell of its own to find. */
export function cellKeyForChain(target: {kind: 'tile'; tileId: number} | {kind: 'scratch'; rootId: string}): string {
    return target.kind === 'tile' ? tileCellKey(target.tileId) : scratchCellKey(target.rootId)
}

export const GHOST_CELL: WallCell = {kind: 'ghost', key: 'ghost', width: 2, height: 3}

/** A visitor's cell — its own dimensions off the wire `AnonTile`. */
export function anonCell(tile: AnonTile): WallCell {
    return {kind: 'anon', key: `a${tile.id}`, tile, width: tile.width, height: tile.height}
}

/**
 * The cells of a closed house's visitor page (plan `2026_09_17_ovid_one_wall.md` § Decided 1) — the
 * wire's own order, one cell per tile. A visitor has no ghost: the house's ending (`Wall`'s `footer`)
 * is where they are asked.
 *
 * When the wire shows only its first tiles clear, the band a visitor walks ends with them and the
 * blurred tiles after them are the coda; otherwise the band is the composition. `codaFrom` is the
 * index the coda begins at, which is what `justify.boundedRows` counts its rows from.
 */
export function anonCells(page: AnonWallResponse): WallCells {
    const cells = page.tiles.map(anonCell)
    const clear = page.tiles.filter((tile) => 'image_key' in tile).length
    const band = clear > 0 && clear < page.tiles.length ? Math.min(page.composition_count, clear) : page.composition_count
    // A visitor owns no row, so nothing arrived and nothing is stamped — `POST /seen` is an
    // authenticated route the wall never reaches while signed out.
    return {cells, arrivalTakeIds: [], arrivalCellCount: 0, codaFrom: Math.min(Math.max(band, 0), cells.length)}
}

const DAY_MS = 24 * 60 * 60 * 1000

/** `T` in the report's formula (`T × d`) — the spacing, in catalog tiles, between one day's group
 *  and the next (report § 4: "starts near one desktop row, about six tiles"). A dial: raise it to
 *  spread days further apart, lower it to bunch them tighter against the top. */
export const DAY_GROUP_SPACING = 6

/** `seen_at` (null ⇒ day 0) to a day index, clamped at zero. `now` can be EARLIER than a `seen_at`
 *  set during the same page session (a stamp lands after the page's own frozen `now`, never
 *  before) — clamping rather than letting that go negative is what keeps a freshly-stamped row in
 *  day 0 instead of sliding into an impossible "day -1" ahead of it. */
function dayOf(seenAt: string | null, now: number): number {
    if (seenAt === null) return 0
    const seenMs = Date.parse(seenAt)
    if (Number.isNaN(seenMs)) return 0
    return Math.max(0, Math.floor((now - seenMs) / DAY_MS))
}

export type WallCellsInput = {
    /** The tiles of the loaded pages, in the wire's order. */
    tiles: readonly TileSummary[]
    /** Index into `tiles` where the ghost belongs, or null when the seam is not reached yet. */
    seamAt: number | null
    /** The query the tiles on screen ANSWER — never the one being typed (`useWallPages.loadedQuery`;
     *  the wall re-arranges once, at the page swap). */
    query: string
    /** Read once per entry (`Wall.tsx`'s held snapshot) — see this file's header on why that, not
     *  a live clock, is what keeps a chain from moving while the page stays open. Unused under a
     *  query, where nothing is scored. */
    now: number
    /** `/mine`'s own `done` — the newest done row of every own chain, one per chain by the server's
     *  DISTINCT ON; collapsed again here (`collapseChains`) for the same reason every other reader
     *  in this file does: the wall's cells are their own statement of the canon, not an inheritance
     *  from the wire. Empty for a signed-out visitor or one with nothing of their own — the rest
     *  branch then degrades to the ghost plus the unweighted catalog (BRIEF §8) with no special
     *  case needed for either. */
    ownDone: readonly Take[]
    /** `/mine?q=`'s own `matched` array — every chain (armed or from-scratch) the SERVER found by
     *  `tsv @@ query` (direction and served prompt both), newest chain first. Not filtered again
     *  here — `mine.server.ts` is the one parser now, this file only paints what it returned. */
    ownMatched: readonly Take[]
    /** `/mine.pinned` — every own take with `pinnedAt` set, one entry per PIN (not per chain: a
     *  chain may hold two). Unfiltered by `q` on the wire (`contract.ts`'s own doc), so this is the
     *  same array in both branches below — the rest branch places every one of them by its own day;
     *  the query branch paints one only when its chain is already in `ownMatched` (BRIEF Iteration
     *  II §4 edge case 7: "pinned cells match on their own row's tsv like any own row"). Either way,
     *  a pinned take equal to its chain's current face (`chainFaces`) is skipped — the chain's own
     *  cell already shows it. */
    ownPinned: readonly Take[]
    /** `GET /mine?tiles=` rows — the faces of chains beyond `/mine`'s window. Never placed; read
     *  only to tell a pinned take that is such a chain's face. */
    ownByTile?: readonly Take[]
    /** A tile summary by id, for a chain whose root is not on a loaded page (the row's own joined
     *  `Take.root` is tried first; the caller fetches it through `GET /tile/:id` on a miss, same
     *  path the arrival block and the armed basis step already use). */
    tileById: (id: number) => TileSummary | null
    /** `chains.batchTakes` — the `?batch=` block (plan e): the nine cells of the batch the page verb
     *  just wrote, in POSITION order, leading day 0's group so the visitor lands on the nine they
     *  asked for rather than on nine rows sorted by a `created_at` they all share. Empty on every
     *  other entry to the wall, which is the shipped behaviour unchanged. */
    batch?: readonly Take[]
    /** Cells born within the current entry (`bornTakes`), newest first — painted right after the
     *  ghost in both orderings, ahead of everything the snapshot placed. An insertion: the
     *  snapshot's own positions are counted as if they were not there, so the catalog and the day
     *  groups keep their order and simply sit further along. */
    born?: readonly Born[]
}

/** A cell that came to exist within an entry: a chain's face (a scratch chain's first take, a chain
 *  rooted on a tile no loaded page carries) or a take pinned now. */
export type Born = {kind: 'chain' | 'pinned'; take: Take}

export type WallCells = {
    cells: WallCell[]
    /** Every unseen row painted at day 0 (batch included) — what `POST /seen` stamps. Unchanged in
     *  meaning from before Iteration II: a chain's older rows are not part of this, so none is left
     *  unstamped to lift its chain again on the next visit. */
    arrivalTakeIds: string[]
    /** How many of day 0's cells are UNSEEN — a same-day but already-seen chain (ordered after the
     *  unseen ones within day 0, § header) does not count, since nothing arrived for it this visit.
     *  This is what `wall_arrival_seen` reports. */
    arrivalCellCount: number
    /** Where a closed house's coda begins (`anonCells`) — the index `justify.boundedRows` counts
     *  its rows from. Absent on an open wall, which has no end to bound. */
    codaFrom?: number
}

/** The cells of `born`, and what they claim from the snapshot's own placement: the chains and
 *  tiles they paint (so neither is painted twice), the pinned takes, and the unseen done faces the
 *  stamp reads. A born chain whose tile is not known yet paints nothing, same as everywhere else. */
function bornPlacement(born: readonly Born[], tileById: (id: number) => TileSummary | null) {
    const cells: WallCell[] = []
    const chainKeys = new Set<ChainKey>()
    const tileIds = new Set<number>()
    const pinnedIds = new Set<string>()
    const arrivalIds: string[] = []
    for (const {kind, take} of born) {
        if (kind === 'pinned') {
            cells.push(pinnedCell(take))
            pinnedIds.add(take.id)
            continue
        }
        if (take.root_tile_id === null) {
            cells.push(scratchCell(take))
        } else {
            const tile = take.root ?? tileById(take.root_tile_id)
            if (!tile) continue
            cells.push(tileCell(tile))
            tileIds.add(tile.id)
        }
        chainKeys.add(chainKeyOf(take))
        if (take.status === 'done' && take.seen_at === null) arrivalIds.push(take.id)
    }
    return {cells, chainKeys, tileIds, pinnedIds, arrivalIds}
}

export function wallCells({
    tiles, seamAt, query, now, ownDone, ownMatched, ownPinned, ownByTile = [], tileById, batch = [], born = [],
}: WallCellsInput): WallCells {
    const newborn = bornPlacement(born, tileById)

    if (query.trim().length > 0) {
        const seamIndex = seamAt ?? tiles.length

        // The user's own matched chains lead — one cell per chain (`collapseChains`, same guard
        // the arrival block applies to `/mine`'s own rows). A tile-rooted match paints the tile's
        // cell (the take covers its picture at render, same as everywhere else); one not yet known
        // to `tileById` (its own basis tile off every loaded page) is skipped here exactly as the
        // arrival block skips one — the caller's `GET /tile/:id` fetch, once it lands, is what
        // brings the next render's `tileById` current.
        const own: WallCell[] = []
        const ownTileIds = new Set<number>(newborn.tileIds)
        const matchedChainKeys = new Set<ChainKey>()
        for (const take of collapseChains(ownMatched)) {
            matchedChainKeys.add(chainKeyOf(take))
            if (newborn.chainKeys.has(chainKeyOf(take))) continue
            if (take.root_tile_id === null) {
                own.push(scratchCell(take))
                continue
            }
            const tile = take.root ?? tileById(take.root_tile_id)
            if (!tile) continue
            own.push(tileCell(tile))
            ownTileIds.add(tile.id)
        }

        // A pinned take is an own row like any other, so a chain search already found also shows
        // its pinned step (edge case 7) — the same face-skip rule as at rest, `chainFaces` read off
        // `ownMatched` here since that IS each matched chain's current face under a query.
        const matchedFaces = chainFaces(ownMatched)
        for (const take of ownPinned) {
            if (!matchedChainKeys.has(chainKeyOf(take))) continue
            if (isFace(take, matchedFaces)) continue
            if (newborn.pinnedIds.has(take.id)) continue
            own.push(pinnedCell(take))
        }

        // A tile the own-match block already leads with is removed from wherever the catalog would
        // otherwise place it — the same one-cell-per-tile rule the arrival block applies to
        // `liftedTileIds` below. Filtered AFTER slicing on `seamIndex` (an index into the ORIGINAL
        // `tiles`, computed server-side from the catalog's own matched count): removing entries
        // before slicing would shift that boundary.
        const matchedCatalog = tiles.slice(0, seamIndex).filter((t) => !ownTileIds.has(t.id))
        const restCatalog = tiles.slice(seamIndex).filter((t) => !ownTileIds.has(t.id))

        return {
            cells: [
                GHOST_CELL,
                ...newborn.cells,
                ...own,
                ...matchedCatalog.map(tileCell),
                ...restCatalog.map(tileCell),
            ],
            // Nothing is stamped while a query is answered (BRIEF §3): clearing the field must
            // bring day 0 back exactly where the stamp would have left it.
            arrivalTakeIds: [],
            arrivalCellCount: 0,
        }
    }

    // ── rest (empty query) ─────────────────────────────────────────────────────────────────────
    // The ghost is position 0 of the whole, before day 0, always (BRIEF Iteration II §2/7) — signed
    // out or with nothing of one's own, what follows it is simply the catalog (§8), which is
    // exactly what the loop below produces when `ownDone`/`batch` are both empty: no day ever gets
    // opened, so every tile is emitted by the trailing "whatever the catalog has left" pass in its
    // own wire order — no separate signed-out branch needed.
    const tileIndex = new Map<number, number>()
    tiles.forEach((tile, i) => tileIndex.set(tile.id, i))

    // The `?batch=` block leads day 0, in position order, whatever each of its nine rows' own
    // status — a page verb's nine are one piece of work, painted together the moment they arrive
    // (queued, rendering or done alike), never scattered by the day sort's own newest-first rule.
    const day0Batch: WallCell[] = []
    const claimedChains = new Set<ChainKey>(newborn.chainKeys)
    const claimedTileIds = new Set<number>(newborn.tileIds)
    const batchArrivalIds: string[] = []
    for (const take of batch) {
        if (take.root_tile_id === null) continue // a batch row is always rooted on its basis tile
        if (claimedChains.has(chainKeyOf(take))) continue
        const tile = take.root ?? tileById(take.root_tile_id)
        if (!tile) continue
        day0Batch.push(tileCell(tile))
        claimedChains.add(chainKeyOf(take))
        claimedTileIds.add(tile.id)
        // Only a landed, unseen row is stamped — a queued/rendering one has no picture yet to have
        // been seen, and stamping it would be meaningless.
        if (take.status === 'done' && take.seen_at === null) batchArrivalIds.push(take.id)
    }

    // Every OTHER own chain, bucketed by day. `root_tile_id`/`root_id` key a chain
    // (`chainKeyOf`); `collapseChains` gives one row per chain even though `/mine.done` already
    // does, for the same reason every other reader in this file re-asserts it.
    //
    // `pinnedTake` entries join the SAME day map (plan `2026_09_04_wall_pin.md`): a pinned take is
    // clocked on `pinnedAt` rather than `seen_at`, is never a batch/claimedChains concern (it is a
    // second, distinct cell of its chain, not the chain's own face cell), and is skipped outright
    // when it currently equals its chain's face (`chainFaces(ownDone)` — the chain's own cell in
    // this same day-or-catalog placement already shows it). The face of a chain beyond `/mine`'s
    // window (known only from `ownByTile`) has no own cell to defer to: its pinned cell is painted
    // and takes the tile's catalog slot, so the picture appears once, at its pin's day.
    type Entry =
        | {kind: 'chain'; take: Take; unseen: boolean; clock: number | null}
        | {kind: 'pinnedTake'; take: Take; clock: number}
    const byDay = new Map<number, Entry[]>()
    for (const take of collapseChains(ownDone)) {
        if (claimedChains.has(chainKeyOf(take))) continue // already painted via the batch
        const day = dayOf(take.seen_at, now)
        const entry: Entry = {
            kind: 'chain', take, unseen: take.seen_at === null,
            clock: take.seen_at ? Date.parse(take.seen_at) : null,
        }
        const list = byDay.get(day)
        if (list) list.push(entry)
        else byDay.set(day, [entry])
    }
    const faces = chainFaces(ownDone)
    const facesBeyond = chainFaces([...ownDone, ...ownByTile])
    for (const take of ownPinned) {
        if (isFace(take, faces)) continue // is the face; not painted twice
        if (newborn.pinnedIds.has(take.id)) continue
        if (take.root_tile_id !== null && isFace(take, facesBeyond)) claimedTileIds.add(take.root_tile_id)
        const pinnedMs = take.pinned_at ? Date.parse(take.pinned_at) : NaN
        if (Number.isNaN(pinnedMs)) continue // defensive: every row here should carry a pinnedAt
        const day = dayOf(take.pinned_at, now)
        const entry: Entry = {kind: 'pinnedTake', take, clock: pinnedMs}
        const list = byDay.get(day)
        if (list) list.push(entry)
        else byDay.set(day, [entry])
    }
    for (const list of byDay.values()) {
        // Within a day, every own cell sorts by its OWN clock, descending, null (unseen) first
        // (plan § "Order inside a day"): a chain's `unseen` (no `seen_at` yet) and a pinned take
        // (whose `pinnedAt` is never null) are compared the same way — two clocked entries by their
        // clock value, an unclocked one ahead of any clocked one, and two unclocked ones (both
        // unseen chains) tie-broken by `created_at` descending, the rule this list already carried
        // before pins existed.
        list.sort((a, b) => {
            if (a.clock === null && b.clock === null) return at(b.take) - at(a.take)
            if (a.clock === null) return -1
            if (b.clock === null) return 1
            return b.clock - a.clock
        })
    }

    // Day 0 always exists once there is a batch to lead it, even with no other own chain unseen or
    // seen today.
    const days = [...byDay.keys()]
    if (day0Batch.length > 0 && !days.includes(0)) days.push(0)
    days.sort((a, b) => a - b)

    type DayPlan = {cells: WallCell[]; target: number; unseenCount: number; arrivalIds: string[]}
    const plans: DayPlan[] = []
    let cursor = 0
    for (const day of days) {
        // Day 0 is unconditionally position 0 (report § 4); every later day is `T × d`, or right
        // after the previous group's own end when that already runs past it.
        const target = day === 0 ? 0 : Math.max(DAY_GROUP_SPACING * day, cursor)
        const cells: WallCell[] = day === 0 ? [...day0Batch] : []
        const arrivalIds: string[] = day === 0 ? [...batchArrivalIds] : []
        let unseenCount = day === 0 ? day0Batch.length : 0 // the batch's own rows count as unseen (§ header)

        for (const entry of byDay.get(day) ?? []) {
            if (entry.kind === 'pinnedTake') {
                // A pinned cell is its own view of a take, never a catalog position to fall back
                // to and never stamped by the arrival path (edge case 11 — a pin is not an
                // arrival), so it is simply painted here, whatever its chain's own placement.
                cells.push(pinnedCell(entry.take))
                continue
            }
            const {take, unseen} = entry
            if (take.root_tile_id === null) {
                // A scratch chain has no catalog position of its own — always lifted (Iteration II
                // §1, "the seen scratch chain is not placed" rule struck).
                cells.push(scratchCell(take))
            } else {
                const basisIndex = tileIndex.get(take.root_tile_id)
                // "The earlier of its day position and its basis tile's own position" (report § 4):
                // a tile already ahead of where the day would place it simply stays there — it is
                // not lifted, and the loop below paints it in its ordinary catalog slot.
                if (basisIndex !== undefined && basisIndex < target) continue
                const tile = take.root ?? tileById(take.root_tile_id)
                if (!tile) continue // nothing to paint it from; not stamped either
                cells.push(tileCell(tile))
                claimedTileIds.add(tile.id)
            }
            if (unseen) {
                arrivalIds.push(take.id)
                unseenCount += 1
            }
        }

        plans.push({cells, target, unseenCount, arrivalIds})
        cursor = target + cells.length
    }

    // One forward walk of the catalog order, splicing each day's group in at its target and
    // filling every other position from the tiles no group claimed (report § 4: "the catalog fills
    // every position the groups do not take").
    // The born cells stand outside `logicalPos`: an insertion after the ghost, so every day target
    // below still counts catalog-order positions exactly as the snapshot did.
    const out: WallCell[] = [GHOST_CELL, ...newborn.cells]
    let catalogAt = 0
    let logicalPos = 0
    function fillTo(target: number) {
        while (logicalPos < target && catalogAt < tiles.length) {
            const tile = tiles[catalogAt]
            catalogAt += 1
            if (claimedTileIds.has(tile.id)) continue // lifted into a group; painted there instead
            out.push(tileCell(tile))
            logicalPos += 1
        }
    }
    const arrivalTakeIds: string[] = [...newborn.arrivalIds]
    let arrivalCellCount = newborn.arrivalIds.length
    for (const plan of plans) {
        fillTo(plan.target)
        out.push(...plan.cells)
        logicalPos += plan.cells.length
        arrivalTakeIds.push(...plan.arrivalIds)
        arrivalCellCount += plan.unseenCount
    }
    while (catalogAt < tiles.length) {
        const tile = tiles[catalogAt]
        catalogAt += 1
        if (claimedTileIds.has(tile.id)) continue
        out.push(tileCell(tile))
    }

    return {cells: out, arrivalTakeIds, arrivalCellCount}
}

/**
 * The held pins less every pin removed since the entry (plan `2026_09_26_wall_hold_still_pill.md`,
 * ruling 2026-09-26): a cell made now appears now, a cell unmade now disappears now, and nothing
 * else moves — the cells after it close the gap by one, as a born cell opened it by one. Applied to
 * both the snapshot's pins and the born ones, against the live `/mine.pinned`.
 */
export function withoutUnpinned(
    held: {ownPinned: readonly Take[]; born: readonly Born[]},
    livePinned: readonly Take[],
): {ownPinned: Take[]; born: Born[]} {
    const live = new Set(livePinned.map((t) => t.id))
    return {
        ownPinned: held.ownPinned.filter((t) => live.has(t.id)),
        born: held.born.filter((b) => b.kind !== 'pinned' || live.has(b.take.id)),
    }
}

export type BornInput = {
    /** The cells painted now — the snapshot's placement plus every cell already born within this
     *  entry. A candidate whose cell key is among them has a place, and is never born. */
    painted: readonly WallCell[]
    /** The painted query (`WallCellsInput.query`): at rest the own chains are `/mine.done`, under a
     *  query they are `/mine?q=`'s `matched`. */
    query: string
    /** The LIVE `/mine` — the current poll, not the snapshot. */
    ownDone: readonly Take[]
    ownMatched: readonly Take[]
    ownPinned: readonly Take[]
    /** The snapshot's pinned takes. A take pinned before the entry is the snapshot's to place (or to
     *  skip as its chain's face); only a pin made within the entry is born. */
    heldPinned: readonly Take[]
    tileById: (id: number) => TileSummary | null
}

/**
 * What comes to exist within an entry with no place in the painted set (plan
 * `2026_09_26_wall_hold_still_pill.md` § Born cells), newest first: a from-scratch chain's first done
 * take, a take pinned now, a chain rooted on a tile no loaded page carries and the snapshot did not
 * place. A chain whose cell already exists is never born — that cell paints its live face. Pure: the
 * caller keeps what this returns and hands it back to `wallCells` as `born` until the next entry.
 */
export function bornTakes({painted, query, ownDone, ownMatched, ownPinned, heldPinned, tileById}: BornInput): Born[] {
    const placed = new Set(painted.map((c) => c.key))
    const chainsNow = query.trim().length > 0 ? ownMatched : ownDone
    const out: {born: Born; clock: number}[] = []

    for (const take of collapseChains(chainsNow)) {
        const key = take.root_tile_id === null ? scratchCellKey(take.root_id) : tileCellKey(take.root_tile_id)
        if (placed.has(key)) continue
        if (take.root_tile_id !== null && !(take.root ?? tileById(take.root_tile_id))) continue
        out.push({born: {kind: 'chain', take}, clock: at(take)})
    }

    // Under a query a pin is painted only with its matched chain — the same rule `wallCells` keeps.
    const heldIds = new Set(heldPinned.map((t) => t.id))
    const faces = chainFaces(chainsNow)
    const underQuery = query.trim().length > 0
    for (const take of ownPinned) {
        if (heldIds.has(take.id)) continue
        if (placed.has(pinnedCellKey(take.id))) continue
        if (isFace(take, faces)) continue // the chain's own cell already shows it
        if (underQuery && !faces.has(chainKeyOf(take))) continue
        const pinnedMs = take.pinned_at ? Date.parse(take.pinned_at) : NaN
        out.push({born: {kind: 'pinned', take}, clock: Number.isNaN(pinnedMs) ? at(take) : pinnedMs})
    }

    return out.sort((a, b) => b.clock - a.clock).map((o) => o.born)
}
